import {
  type DefaultError,
  type DefaultedInfiniteQueryObserverOptions,
  InfiniteQueryObserver,
  type QueryClient,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  notifyManager,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo, useSyncExternalStore } from 'react';

export interface InfiniteQueryObserverEntry<
  Key,
  QueryFnData,
  Error,
  Data,
  QueryKeyType extends QueryKey,
  PageParam,
> {
  key: Key;
  options: UseInfiniteQueryOptions<QueryFnData, Error, Data, QueryKeyType, PageParam>;
}

type ObserverRecord<Key, QueryFnData, Error, Data, QueryKeyType extends QueryKey, PageParam> = {
  key: Key;
  observer: InfiniteQueryObserver<QueryFnData, Error, Data, QueryKeyType, PageParam>;
  options: DefaultedInfiniteQueryObserverOptions<QueryFnData, Error, Data, QueryKeyType, PageParam>;
};

/**
 * Adapts a dynamic group of infinite-query observers to React's external-store
 * contract. This is deliberately limited to composite virtual views, where
 * React Query has no `useInfiniteQueries` counterpart to `useQueries`.
 */
class InfiniteQueryObserverStore<
  Key,
  QueryFnData,
  Error,
  Data,
  QueryKeyType extends QueryKey,
  PageParam,
> {
  private readonly records: ObserverRecord<
    Key,
    QueryFnData,
    Error,
    Data,
    QueryKeyType,
    PageParam
  >[];
  private results: UseInfiniteQueryResult<Data, Error>[];
  private readonly listeners = new Set<() => void>();
  private unsubscribers: (() => void)[] = [];

  constructor(
    queryClient: QueryClient,
    entries: readonly InfiniteQueryObserverEntry<
      Key,
      QueryFnData,
      Error,
      Data,
      QueryKeyType,
      PageParam
    >[],
  ) {
    this.records = entries.map(({ key, options }) => {
      const defaultedOptions = queryClient.defaultQueryOptions(
        options,
      ) as DefaultedInfiniteQueryObserverOptions<QueryFnData, Error, Data, QueryKeyType, PageParam>;
      defaultedOptions._optimisticResults = 'optimistic';
      const observer = new InfiniteQueryObserver(queryClient, defaultedOptions);
      return { key, observer, options: defaultedOptions };
    });
    this.results = this.records.map(({ observer, options }) =>
      observer.getOptimisticResult(options),
    );
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    if (this.listeners.size === 1) {
      this.unsubscribers = this.records.map(({ observer }) =>
        observer.subscribe(notifyManager.batchCalls(this.update)),
      );
      this.update();
    }

    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) this.unsubscribeAll();
    };
  };

  getSnapshot = () => this.results;

  destroy() {
    this.unsubscribeAll();
    for (const { observer } of this.records) observer.destroy();
  }

  private update = () => {
    const nextResults = this.records.map(({ observer }) => observer.getCurrentResult());
    const changed = nextResults.some((result, index) => result !== this.results[index]);
    if (!changed) return;

    this.results = nextResults;
    for (const listener of this.listeners) listener();
  };

  private unsubscribeAll() {
    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers = [];
  }
}

/**
 * Observes a keyed, dynamic set of infinite queries for a composite virtual
 * view. Callers decide which branch set should stay active, such as all
 * expanded branches in a tree.
 */
export function useInfiniteQueryObservers<
  Key,
  QueryFnData,
  Error = DefaultError,
  Data = QueryFnData,
  QueryKeyType extends QueryKey = QueryKey,
  PageParam = unknown,
>(
  entries: readonly InfiniteQueryObserverEntry<
    Key,
    QueryFnData,
    Error,
    Data,
    QueryKeyType,
    PageParam
  >[],
): ReadonlyMap<Key, UseInfiniteQueryResult<Data, Error>> {
  const queryClient = useQueryClient();
  const store = useMemo(
    () => new InfiniteQueryObserverStore(queryClient, entries),
    [entries, queryClient],
  );
  const results = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  useEffect(() => () => store.destroy(), [store]);

  return useMemo(() => {
    const queryResults = new Map<Key, UseInfiniteQueryResult<Data, Error>>();
    for (const [index, { key }] of entries.entries()) {
      const result = results[index];
      if (result) queryResults.set(key, result);
    }
    return queryResults;
  }, [entries, results]);
}
